// Copyright © 2025 CATTLEytics Inc.
import './PivotTable.css';

import React from 'react';
import { Table } from 'react-bootstrap';

interface PivotTableProps {
  aggregateFunction?: (values: any[]) => any;
  columnField: string;
  data: any[];
  rowField: string;
  showColumnTotals?: boolean;
  showRowTotals?: boolean;
  valueField: string;
}

export function PivotTable({
  data,
  rowField,
  columnField,
  valueField,
  aggregateFunction = (values): any => values.reduce((sum, val) => sum + (Number(val) || 0), 0),
  showRowTotals = false,
  showColumnTotals = false,
}: PivotTableProps): JSX.Element {
  // Extract unique row and column values
  const rows = [...new Set(data.map((item) => item[rowField]))].sort();
  const columns = [...new Set(data.map((item) => item[columnField]))].sort();

  // Create pivot data structure
  const pivotData: { [row: string]: { [col: string]: any } } = {};

  // Initialize empty pivot table
  rows.forEach((row) => {
    pivotData[row] = {};
    columns.forEach((col) => {
      pivotData[row][col] = [];
    });
  });

  // Populate pivot data
  data.forEach((item) => {
    const row = item[rowField];
    const col = item[columnField];
    const val = item[valueField];

    if (pivotData[row] && pivotData[row][col] !== undefined) {
      pivotData[row][col].push(val);
    }
  });

  // Aggregate values
  rows.forEach((row) => {
    columns.forEach((col) => {
      const values = pivotData[row][col];
      pivotData[row][col] = values.length > 0 ? aggregateFunction(values) : '';
    });
  });

  // Calculate row totals if needed
  const rowTotals: { [row: string]: any } = {};
  if (showRowTotals) {
    rows.forEach((row) => {
      const rowValues: number[] = [];
      columns.forEach((col) => {
        const val = pivotData[row][col];
        if (val !== '' && !isNaN(Number(val))) {
          rowValues.push(Number(val));
        }
      });
      rowTotals[row] = rowValues.length > 0 ? aggregateFunction(rowValues) : '';
    });
  }

  // Calculate column totals if needed
  const columnTotals: { [col: string]: any } = {};
  if (showColumnTotals) {
    columns.forEach((col) => {
      const colValues: number[] = [];
      rows.forEach((row) => {
        const val = pivotData[row][col];
        if (val !== '' && !isNaN(Number(val))) {
          colValues.push(Number(val));
        }
      });
      columnTotals[col] = colValues.length > 0 ? aggregateFunction(colValues) : '';
    });
  }

  // Calculate grand total if both row and column totals are shown
  let grandTotal = '';
  if (showRowTotals && showColumnTotals) {
    const allValues: number[] = [];
    Object.values(rowTotals).forEach((val) => {
      if (val !== '' && !isNaN(Number(val))) {
        allValues.push(Number(val));
      }
    });
    grandTotal = allValues.length > 0 ? aggregateFunction(allValues) : '';
  }

  return (
    <div className="pivot-table-container">
      <Table bordered className="pivot-table" hover responsive>
        <thead>
          <tr>
            <th className="pivot-header-cell">{rowField}</th>
            {columns.map((col) => (
              <th className="pivot-header-cell" key={col}>
                {col}
              </th>
            ))}
            {showRowTotals && <th className="pivot-header-cell">Total (Sum)</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <td className="pivot-row-header">{row}</td>
              {columns.map((col) => (
                <td className="pivot-value-cell" key={`${row}-${col}`}>
                  {pivotData[row][col] || ''}
                </td>
              ))}
              {showRowTotals && (
                <td className="pivot-value-cell pivot-total-cell">{rowTotals[row] || ''}</td>
              )}
            </tr>
          ))}
          {showColumnTotals && (
            <tr className="pivot-total-row">
              <td className="pivot-row-header pivot-total-cell">Total (Sum)</td>
              {columns.map((col) => (
                <td className="pivot-value-cell pivot-total-cell" key={`total-${col}`}>
                  {columnTotals[col] || ''}
                </td>
              ))}
              {showRowTotals && <td className="pivot-value-cell pivot-total-cell">{grandTotal}</td>}
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}
